"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { SpacingType, sectionVariants, ButtonStyleType } from "@/types";
import AutoScroll from "embla-carousel-auto-scroll";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import Link from "next/link";
import { SectionWrapper } from "../components/SectionWraper";

interface Hero6Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
    hasStatus?: boolean;
    status?: {
      statusValue1?: string;
      statusLabel1?: string;
      statusValue2?: string;
      statusLabel2?: string;
    };
  };
  features?: {
    icon?: React.ReactNode;
    title?: string;
    content?: string;
  }[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero6 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  features,
  attributes,
}: Hero6Props) => {
  const gradientBackground = cn(
    "absolute inset-0 bg-gradient-to-t",
    variant === 'A' 
      ? "from-background via-transparent to-background" 
      : variant === 'B' 
        ? "from-muted via-transparent to-muted"
        : "from-background via-transparent to-background"
  );

  const featuredItemsBackground = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper 
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero6"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 6"
    >     
      <div className="container"> 

        <div className="grid items-center md:grid-cols-2 gap-14 lg:gap-20">
          <div className="flex flex-col gap-6 text-center md:text-left">
            {contents?.badge && (
              <Badge variant="outline" bold className="mx-auto md:mx-0">
                {contents?.badge}
              </Badge>
            )}
            {contents?.title && (
              <h1>
                {parse(contents.title)}
              </h1>
            )}
            {contents?.content && (
              <div className="mt-2 paragraph-lg text-muted-foreground">
                {parse(contents?.content)}
              </div>
            )}
            {contents?.hasButton && (
              <div className="mt-2">
                <div className="flex flex-col items-center gap-4 sm:flex-row justify-center md:justify-start">
                  <Button
                    asChild
                    size='lg'
                    className="w-full sm:w-auto"
                    variant={contents?.buttonStyle || "primary"}
                  >
                    <Link href={contents?.buttonLink ?? ""}>{contents?.buttonText}</Link>
                  </Button>
                  {contents?.secondaryButton && (
                    <Button
                      className="w-full sm:w-auto"
                      variant={contents?.secondaryButtonStyle || "outline"}
                      asChild
                      size='lg'
                    >
                      <Link href={contents?.secondaryButtonLink ?? ""}> 
                        {contents?.secondaryButtonText}
                      </Link>
                    </Button>
                  )}
                </div>
                {contents?.microCopy && (
                  <p className="mt-2 paragraph-sm text-muted-foreground">
                    {contents?.microCopy}
                  </p>
                )}
              </div>
            )}
            {contents?.hasStatus && (
              <div className="grid grid-cols-2 mt-2 sm:mt-10 gap-4 sm:gap-10 max-w-[70%] md:max-w-[100%] mx-auto md:mx-0 text-center md:text-left">
                {contents?.status?.statusValue1 && (
                  <div className="flex flex-col gap-1">
                    <h2 className="text-gradient-primary">
                      {contents?.status?.statusValue1}
                    </h2>
                    {contents?.status?.statusLabel1 && (
                      <div className="text-muted-foreground paragraph-base">
                        {parse(contents?.status?.statusLabel1)}
                      </div>
                    )}
                  </div>
                )}
                {contents?.status?.statusValue2 && (
                  <div className="flex flex-col gap-1">
                    <h2 className="text-gradient-primary">
                      {contents?.status?.statusValue2}
                    </h2>
                    {contents?.status?.statusLabel2 && (
                      <div className="text-muted-foreground paragraph-base">
                        {parse(contents?.status?.statusLabel2)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid gap-6 md:gap-8 lg:grid-cols-2">
            <Carousel
              opts={{
                loop: true,
                align: "start",
              }}
              plugins={[
                AutoScroll({
                  speed: 0.7,
                }),
              ]}
              orientation="vertical"
              className="pointer-events-none relative lg:hidden"
            >
              <CarouselContent className="max-h-[600px]">
                {features?.map((feature, index) => (
                  <CarouselItem key={index} className="pt-6">
                    <div className={cn(
                      "flex flex-col rounded-xl p-6 shadow-md",
                      featuredItemsBackground
                    )}>
                      <span className="material-symbols-outlined">{feature.icon}</span>
                      <div className="mt-4 paragraph-lg font-bold">
                        {feature.title}
                      </div>
                      <div className="text-muted-foreground paragraph-base mt-1">
                        {parse(feature.content ?? "")}
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className={gradientBackground}></div>
            </Carousel>
            <Carousel
              opts={{
                loop: true,
                align: "start",
              }}
              plugins={[
                AutoScroll({
                  speed: 0.7,
                }),
              ]}
              orientation="vertical"
              className="pointer-events-none relative hidden lg:block"
            >
              <CarouselContent className="max-h-[600px]">
                {features
                  ?.slice(0, features.length / 2)
                  .map((feature, index) => (
                    <CarouselItem key={index} className="pt-6">
                      <div className={cn(
                        "flex flex-col rounded-xl p-6 shadow-md",
                        featuredItemsBackground
                      )}>
                        <span className="material-symbols-outlined">{feature.icon}</span>
                        <div className="mt-4 paragraph-lg font-bold">
                          {feature.title}
                        </div>
                        <div className="text-muted-foreground paragraph-base mt-1">
                          {parse(feature.content ?? "")}
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
              </CarouselContent>
              <div className={gradientBackground}></div>
            </Carousel>
            <Carousel
              opts={{
                loop: true,
                align: "start",
              }}
              plugins={[
                AutoScroll({
                  speed: 0.7,
                }),
              ]}
              orientation="vertical"
              className="pointer-events-none relative hidden lg:block"
            >
              <CarouselContent className="max-h-[600px]">
                {features?.slice(features.length / 2).map((feature, index) => (
                  <CarouselItem key={index} className="pt-6">
                    <div className={cn(
                      "flex flex-col rounded-xl p-6 shadow-md",
                      featuredItemsBackground
                    )}>
                      <span className="material-symbols-outlined">{feature.icon}</span>
                      <div className="mt-4 paragraph-lg font-bold">
                        {feature.title}
                      </div>
                      <div className="text-muted-foreground paragraph-base mt-1">
                        {parse(feature.content ?? "")}
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className={gradientBackground}></div>
            </Carousel>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero6 };
